import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import RenderImage from '../utils/RenderImage';
import IdChip from './IdChip';
import { Icons } from './Icons';
import { epochToLocalDate } from '../../utils/dateConverter';

/**
 * Reusable "Location Information" card: shows the store hero (reference image,
 * name, code, description, id chip) and a 3-column grid with lat / lon /
 * creation date. Designed to live inside any wrapper that exposes the
 * `--ds-*` design tokens used by `.pn-*` classes (today that means
 * `.payload-new`; future consumers either wrap with the same class or migrate
 * the styles to `.ds-*` primitives — see DESIGN_SYSTEM.md).
 *
 * Page-specific extras (PayloadNew's online/offline pill and PDF button)
 * are not baked in. Pass them via the `actions` slot to keep the component
 * useful in surveys, frauds, or any other context that doesn't share those
 * controls.
 *
 * @param {object} locationInfo        Store/location object with `name`, `code`,
 *                                     `description`, `reference`, `_id`, `lat`,
 *                                     `lon`, `date`. Missing fields render as
 *                                     em-dashes.
 * @param {React.ReactNode} [actions]  Optional JSX slot rendered on the right
 *                                     side of the hero (pills, buttons, etc.).
 * @param {string} [sectionId]         Optional id on the `<section>` for rail
 *                                     anchors (e.g. 'pn-identity').
 * @param {string} [className]         Extra class merged with the outer card.
 */
// `date` arrives either as `{ $date: <epoch_ms> }` (BSON-ish responses) or
// as a plain string like "2022-11-15 04:49:19.829000" (legacy file imports).
// Normalize both to epoch ms so epochToLocalDate has a single shape to work
// with; return null when we can't make sense of it.
const normalizeDateMs = (date) => {
	if (date == null) return null;
	if (typeof date === 'object' && typeof date.$date === 'number') return date.$date;
	if (typeof date === 'string') {
		// Browsers parse "YYYY-MM-DDTHH:mm:ss" reliably; the space-separated
		// variant works on most but not all engines, so swap it for safety.
		const iso = date.includes('T') ? date : date.replace(' ', 'T');
		const ms = new Date(iso).getTime();
		return Number.isFinite(ms) ? ms : null;
	}
	return null;
};

const LocationInfoCard = ({ locationInfo, actions, sectionId, className }) => {
	const { t } = useTranslation();
	const storeId = locationInfo?._id?.$oid ?? locationInfo?._id;
	const refId = locationInfo?.reference?.$oid ?? locationInfo?.reference ?? '';
	const dateMs = normalizeDateMs(locationInfo?.date);
	const createdAt = dateMs !== null
		? epochToLocalDate(dateMs, localStorage.getItem('i18nextLng'))
		: '—';

	return (
		<section
			id={sectionId}
			className={`pn-card pn-card--flush ${className || ''}`.trim()}
		>
			<div className='pn-hero'>
				<div className='pn-hero-photo'>
					<RenderImage
						imageId={refId}
						defaultSrc={`${process.env.PUBLIC_URL}/assets/ref-image.svg`}
						alt='Store reference'
						refImage='reference'
					/>
				</div>
				<div className='pn-hero-body'>
					<div className='pn-hero-top'>
						<div>
							<div className='pn-hero-eyebrow'>
								<Icons.store /> {t('payload.locationInfo.title')}
							</div>
							<h1 className='pn-hero-name'>{locationInfo?.name ?? '—'}</h1>
							{locationInfo?.code && (
								<div className='pn-hero-code'>
									<span className='pn-hero-code-label'>{t('payload.locationInfo.code')}</span>
									<span className='pn-hero-code-value'>{locationInfo.code}</span>
								</div>
							)}
							{locationInfo?.description && (
								<div className='pn-hero-desc'>{locationInfo.description}</div>
							)}
							<div className='pn-hero-chips'>
								<IdChip label='Id' value={storeId} full />
							</div>
						</div>
						{actions && (
							<div className='pn-hero-aside'>
								{actions}
							</div>
						)}
					</div>
					<div className='pn-stat-grid cols-3'>
						<div>
							<div className='pn-stat-label'>{t('payload.locationInfo.lat')}</div>
							<div className='pn-stat-value mono'>{locationInfo?.lat ?? '—'}</div>
						</div>
						<div>
							<div className='pn-stat-label'>{t('payload.locationInfo.lng')}</div>
							<div className='pn-stat-value mono'>{locationInfo?.lon ?? '—'}</div>
						</div>
						<div>
							<div className='pn-stat-label'>{t('payload.locationInfo.creationDate')}</div>
							<div className='pn-stat-value'>{createdAt}</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

LocationInfoCard.propTypes = {
	locationInfo: PropTypes.shape({
		name: PropTypes.string,
		code: PropTypes.string,
		description: PropTypes.string,
		reference: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({ $oid: PropTypes.string }),
		]),
		_id: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({ $oid: PropTypes.string }),
		]),
		lat: PropTypes.number,
		lon: PropTypes.number,
		date: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.shape({ $date: PropTypes.number }),
		]),
	}),
	actions: PropTypes.node,
	sectionId: PropTypes.string,
	className: PropTypes.string,
};

export default LocationInfoCard;
